import { AxiosError } from "axios";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { client } from "../api/client";
import Navbar from "../components/ui/navbar";

interface SuccessModalProps {
  onConfirm: () => void;
}

const SuccessModal: React.FC<SuccessModalProps> = ({ onConfirm }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center w-12 h-12 mb-4 bg-green-100 rounded-full">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Success!</h3>
          <p className="mb-6 text-center text-gray-700">
            Your research data submission request has been received. We will
            review it and get back to you soon.
          </p>
          <button
            onClick={onConfirm}
            className="w-full px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

interface FormData {
  name: string;
  email: string;
  institution: string;
  position: string;
  researchTitle: string;
  doi: string;
  description: string;
  additionalNotes: string;
  files: File[];
}

const DownloadCSVTemplate = () => {
  const templatePath = "LNPDB_deposit_data_template.xlsx";
  return (
    <div className="p-4 mb-4 text-sm text-blue-800 border border-blue-200 rounded-lg bg-blue-50">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center mb-2">
            <svg
              className="flex-shrink-0 w-5 h-5 mr-2 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                clipRule="evenodd"
              ></path>
            </svg>

            <span className="font-semibold">
              Your deposited LNP data should follow the provided template CSV
            </span>
          </div>

          <p className="text-xs ml-7">
            Download the template CSV and enter your LNP data, with each row
            representing one LNP formulation.
          </p>
        </div>

        <a
          href={templatePath}
          download="csv_template.xlsx"
          className="inline-flex items-center px-4 py-2 mt-4 ml-4 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 whitespace-nowrap"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 mr-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download
        </a>
      </div>
    </div>
  );
};

const ResearchSubmissionForm: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    institution: "",
    position: "",
    researchTitle: "",
    doi: "",
    description: "",
    additionalNotes: "",
    files: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files).filter(
        (file) => file.type === "text/csv" || file.name.endsWith(".csv")
      );

      if (filesArray.length === 0) {
        setErrors((prev) => ({
          ...prev,
          files: "Only CSV files are allowed",
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        files: filesArray,
      }));

      if (errors.files) {
        setErrors((prev) => ({
          ...prev,
          files: "",
        }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.position.trim()) newErrors.position = "Position is required";

    if (!formData.institution.trim())
      newErrors.institution = "Institution is required";
    if (!formData.researchTitle.trim())
      newErrors.researchTitle = "Research title is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    if (
      formData.doi.trim() &&
      !/^10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i.test(formData.doi.trim())
    ) {
      newErrors.doi = "Please enter a valid DOI (e.g., 10.1000/xyz123)";
    }

    if (formData.files.length === 0) {
      newErrors.files = "At least one CSV file is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("institution", formData.institution);
      formDataToSend.append("position", formData.position);
      formDataToSend.append("researchTitle", formData.researchTitle);
      formDataToSend.append("doi", formData.doi);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("additionalNotes", formData.additionalNotes);

      formData.files.forEach((file) => {
        formDataToSend.append("files", file);
      });

      await client.post("/search/research/", formDataToSend);

      setShowSuccessModal(true);

      setFormData({
        name: "",
        email: "",
        institution: "",
        position: "",
        researchTitle: "",
        doi: "",
        description: "",
        additionalNotes: "",
        files: [],
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      const axiosErr = error as AxiosError<{ error?: string; detail?: string }>;
      const serverMessage =
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        "An error occurred while submitting the form. Please try again.";
      setErrors((prev) => ({
        ...prev,
        form: serverMessage,
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSuccess = () => {
    navigate("/");
  };

  return (
    <div className="max-w-2xl p-6 m-6 mx-auto bg-white border border-gray-200 rounded-lg shadow-lg">
      <h2 className="mb-6 text-2xl font-bold text-gray-900">
        Request to deposit your LNP data
      </h2>

      {showSuccessModal && <SuccessModal onConfirm={handleConfirmSuccess} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.form && (
          <div
            className="p-4 mb-4 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
            role="alert"
          >
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span className="font-medium">Error!</span>
            </div>
            <p className="mt-2">{errors.form}</p>
          </div>
        )}

        <div
          className="flex p-4 mb-4 text-sm text-blue-800 border border-blue-200 rounded-lg bg-blue-50"
          role="alert"
        >
          <svg
            className="flex-shrink-0 w-5 h-5 mr-2 text-blue-600"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
              clipRule="evenodd"
            ></path>
          </svg>
          <div>
            <p className="font-medium">
              Request to deposit your LNP data into LNPDB. After submission, the
              LNPDB team will review the data and email you if clarification or
              additional information is needed.
            </p>
          </div>
        </div>

        <DownloadCSVTemplate />

        <div
          className="flex items-start p-4 mb-4 text-sm text-blue-800 border border-blue-200 rounded-lg bg-blue-50"
          role="alert"
        >
          <svg
            className="flex-shrink-0 w-5 h-5 mr-2 text-blue-600"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
              clipRule="evenodd"
            ></path>
          </svg>
          <div>
            <span className="font-semibold">Additional Tips:</span>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                Please maintain the template’s column structure. If you feel the
                need to add a new column, please provide clear justification in
                the Additional Notes box below.
              </li>
              <li>
                Provide clear explanations of your experimental methods,
                results, and any post-processing steps.
              </li>
            </ul>
          </div>
        </div>

        <div className="p-5 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Researcher Information
          </h3>

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label
                htmlFor="name"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="mt-5">
            <label
              htmlFor="institution"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Institution/Organization <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="institution"
              name="institution"
              value={formData.institution}
              onChange={handleInputChange}
              className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                errors.institution ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter your institution or organization"
            />
            {errors.institution && (
              <p className="mt-1 text-sm text-red-600">{errors.institution}</p>
            )}
          </div>
          <div className="mt-5">
            <label
              htmlFor="position"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Position <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="position"
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                errors.position ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter your position (e.g., Graduate student, Postdoc, PI)"
            />
            {errors.position && (
              <p className="mt-1 text-sm text-red-600">{errors.position}</p>
            )}
          </div>
        </div>

        <div className="p-5 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Research Information
          </h3>

          <div className="mb-5">
            <label
              htmlFor="researchTitle"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Research Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="researchTitle"
              name="researchTitle"
              value={formData.researchTitle}
              onChange={handleInputChange}
              className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                errors.researchTitle ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter the title of your research"
            />
            {errors.researchTitle && (
              <p className="mt-1 text-sm text-red-600">
                {errors.researchTitle}
              </p>
            )}
          </div>

          <div className="mb-5">
            <label
              htmlFor="doi"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              DOI (Digital Object Identifier)
            </label>
            <input
              type="text"
              id="doi"
              name="doi"
              value={formData.doi}
              onChange={handleInputChange}
              className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                errors.doi ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="e.g., 10.1000/xyz123"
            />
            {errors.doi && (
              <p className="mt-1 text-sm text-red-600">{errors.doi}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              If your research is published, please provide the DOI.
            </p>
          </div>

          <div>
            <label
              htmlFor="description"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Research Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                errors.description ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Provide a brief description of your research and the data you're submitting"
            ></textarea>
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>
        </div>

        <div className="p-5 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Data File <span className="text-red-500">*</span>
          </h3>

          <div className="mb-3">
            <label
              className="block mb-2 text-sm font-medium text-gray-900"
              htmlFor="files"
            >
              Upload CSV or Excel file
            </label>
            <input
              type="file"
              id="files"
              name="files"
              onChange={handleFileChange}
              accept=".csv,.xlsx"
              multiple
              className="block w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-lg cursor-pointer focus:outline-none"
            />
            {errors.files && (
              <p className="mt-1 text-sm text-red-600">{errors.files}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Only CSV files are accepted. Maximum size: 50MB per file.
            </p>
          </div>

          {formData.files.length > 0 && (
            <div className="p-3 mt-4 bg-gray-100 rounded-lg">
              <h4 className="mb-2 text-sm font-medium text-gray-900">
                Selected Files:
              </h4>
              <ul className="max-w-md space-y-1 text-gray-500 list-disc list-inside">
                {formData.files.map((file, index) => (
                  <li key={index} className="text-sm">
                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="p-5 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Additional Information
          </h3>

          <div>
            <label
              htmlFor="additionalNotes"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Additional Notes
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              rows={3}
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              placeholder="Any additional information you'd like to provide"
            ></textarea>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              required
              className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="font-medium text-gray-900">
              I agree to the Terms and Conditions and consent to having our
              research data included in the LNPDB.
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`text-white font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none ${
              isSubmitting
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300"
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <svg
                  className="inline w-4 h-4 mr-2 text-white animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#E5E7EB"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
                Submitting...
              </div>
            ) : (
              "Submit Request"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

const DepositPage = () => {
  return (
    <>
      <div className="relative flex-shrink-0 w-full py-8 border-b border-1">
        <Navbar />
      </div>
      <div className="flex justify-center">
        <div className="flex-shrink-0 mr-10">
          <ResearchSubmissionForm />
        </div>
      </div>
    </>
  );
};

export default DepositPage;
